/**
 * Open, move within and close "Your basics" through the URL (see
 * `profile-basics-href`). Opening PUSHES a history entry — `open`, or any link
 * to `?basics=…` on the page it opens over — so Back closes the window; moving
 * between its views REPLACES it, so Back never walks through them.
 *
 * Closing undoes exactly what opening did. A window that opened while its
 * page was already showing sits on an entry of its own: closing goes back
 * that one entry. A window that arrived with the page (a link from another
 * page, the old `/profile/complete` address, a reload) has no entry of its
 * own to go back from: closing drops the parameter in place.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearch } from 'wouter';

import {
  profileBasicsViewFrom,
  withProfileBasics,
  type ProfileBasicsView,
} from '../lib/profile-basics-href';

export function useProfileBasicsRoute() {
  const [path, navigate] = useLocation();
  const search = useSearch();
  const view = useMemo(() => profileBasicsViewFrom(search), [search]);

  const previous = useRef(view);
  const openedInPlace = useRef(false);
  useEffect(() => {
    if (previous.current === null && view !== null) openedInPlace.current = true;
    if (view === null) openedInPlace.current = false;
    previous.current = view;
  }, [view]);

  const open = useCallback(
    (next: ProfileBasicsView = 'form') => {
      navigate(`${path}${withProfileBasics(search, next)}`);
    },
    [navigate, path, search]
  );

  const show = useCallback(
    (next: ProfileBasicsView) => {
      navigate(`${path}${withProfileBasics(search, next)}`, { replace: true });
    },
    [navigate, path, search]
  );

  const close = useCallback(() => {
    if (openedInPlace.current) {
      window.history.back();
      return;
    }
    navigate(`${path}${withProfileBasics(search, null)}`, { replace: true });
  }, [navigate, path, search]);

  return { view, open, show, close };
}
